import { useEffect, useState } from 'react'
import {
  LegalMatterQuoteFragment,
  LegalMatterQuotesFragment,
} from '@generatedTypes/graphql'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Button, TextareaAutosize } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { Errors } from '@/interfaces/DialogControlType'
import SubscriberUser from '@/models/SubscriberUser'
import QuoteListItem from '@components/organisms/QuoteListItem'
import useQuotes from '@utils/useQuotes'
import { useStripePayment } from '@utils/useStripePayment'
import ConfirmationDialog, {
  ConfirmationDialogProps,
} from '../molecules/ConfirmationDialog'
import { enqueueSnackbar } from 'notistack'
import { setSessionItem, SessionStorageKeys } from '@/utils/sessionStorage'
import { GetQuotePaymentInfoDocument } from '@generatedTypes/graphql'
import { useLazyQuery } from '@apollo/client'
import { PaymentInfoDisplay } from '../atoms/PaymentInfoDisplay'
import { formattedAmount, toDollars } from '@/utils/stripeUtils'
import { useStripePaymentContext } from '@view/providers/StripePaymentProvider'

interface SubscriberQuoteAdminProps {
  currentUser?: SubscriberUser
  legalMatterKey?: string
  fragment: LegalMatterQuotesFragment
  readOnly: boolean
  sx?: SxProps<Theme>
}
interface DialogControlProps extends ConfirmationDialogProps {
  errors?: Errors
  textBoxContent?: string
}

const SubscriberQuoteAdmin = ({
  currentUser,
  legalMatterKey,
  fragment,
  readOnly,
  sx = {},
}: SubscriberQuoteAdminProps) => {
  const { acceptQuote, declineQuote, quoteLoading } = useQuotes(legalMatterKey)
  const { setupPaymentMethod, closePayment } = useStripePayment()
  const [fetchPaymentInfo, { data: paymentData }] = useLazyQuery(
    GetQuotePaymentInfoDocument,
  )
  const { cardInfo, setCardInfo } = useStripePaymentContext()

  const [acceptedQuote, setAcceptedQuote] = useState<
    LegalMatterQuoteFragment | undefined
  >(undefined)

  const handleQuoteAcceptance = (quoteKey: string) => {
    closePayment()
    // Stripe has a requirement for min change ammounts of 0.5 USD
    // We need to make sure payload being send fills this requirement
    // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
    void acceptQuote({
      variables: { quoteKey },
      onCompleted: async (data) => {
        if (
          data.quoteAccept.__typename === 'QuoteAcceptMutationResponse' &&
          data.quoteAccept.success
        ) {
          setSessionItem(
            SessionStorageKeys.QuotePaymentIntent,
            data.quoteAccept.clientSecret,
          )
          setSessionItem(SessionStorageKeys.QuoteKey, quoteKey)

          if (!cardInfo?.autoConfirm) {
            void setupPaymentMethod({
              subscriberKey: currentUser?.key ?? '',
            })
          }
          setDialogControlProps(dialogControlIntialState)
        } else {
          enqueueSnackbar(
            `Quote acceptance failed: ${data.quoteAccept.message}`,
            { variant: 'error' },
          )
        }
      },
      onError: (error) => {
        enqueueSnackbar(
          `Quote acceptance failed: ${error.message}, please try again in a few seconds`,
          { variant: 'error' },
        )
      },
    })
  }

  const quotes = fragment.quotes.edges.map((edge) => edge.node)

  const dialogControlIntialState: DialogControlProps = {
    open: false,
    title: <></>,
    content: <></>,
    actions: <></>,
    textBoxContent: undefined,
    errors: {
      message: undefined,
    },
  }

  const [dialogControlProps, setDialogControlProps] = useState(
    dialogControlIntialState,
  )

  const handleQuoteAcceptConfirmation = (
    quote: LegalMatterQuoteFragment,
  ): void => {
    fetchPaymentInfo({ variables: { quoteKey: quote.key } })
    setAcceptedQuote(quote)
  }

  useEffect(() => {
    if (paymentData?.getQuotePaymentInfo) {
      setCardInfo(paymentData.getQuotePaymentInfo)
    }
  }, [paymentData, setCardInfo])

  useEffect(() => {
    if (!!acceptedQuote && cardInfo?.autoConfirm === true) {
      setDialogControlProps((prevState) => ({
        ...prevState,
        open: true,
        title: (
          <>
            <Typography variant="h1" gutterBottom>
              Confirm Payment
            </Typography>
            <Typography gutterBottom>
              By clicking Pay Now you agree to pay{' '}
              <b>${formattedAmount(toDollars(acceptedQuote.totalAmount))} </b>{' '}
              using the card:
            </Typography>
          </>
        ),
        content: (
          <>
            {!!dialogControlProps.errors?.message && (
              <Alert variant="outlined" severity="error">
                {dialogControlProps.errors.message}
              </Alert>
            )}
            <PaymentInfoDisplay
              type={cardInfo?.type}
              endNum={cardInfo?.endNum}
              month={cardInfo?.month}
              year={cardInfo?.year}
            />
          </>
        ),
        actions: (
          <>
            <Button
              onClick={() => {
                setDialogControlProps(dialogControlIntialState)
                setAcceptedQuote(undefined)
              }}
              autoFocus
              variant="contained"
              color="error"
              data-testid="confirm-dialog-btn-cancel"
              loading={quoteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleQuoteAcceptance(acceptedQuote.key)
              }}
              loading={quoteLoading}
              autoFocus
              variant="contained"
              color="primary"
              data-testid="confirm-dialog-btn-accept"
            >
              Pay Now
            </Button>
          </>
        ),
      }))
    }
    // open setup Payment Drawer
    if (!!acceptedQuote && cardInfo?.autoConfirm === false) {
      // Stripe has a requirement for min change ammounts of 0.5 USD
      // We need to make sure payload being send fills this requirement
      // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
      void acceptQuote({
        variables: { quoteKey: acceptedQuote.key },
        onCompleted: async (data) => {
          if (
            data.quoteAccept.__typename === 'QuoteAcceptMutationResponse' &&
            data.quoteAccept.success
          ) {
            setSessionItem(
              SessionStorageKeys.QuotePaymentIntent,
              data.quoteAccept.clientSecret,
            )
            setSessionItem(SessionStorageKeys.QuoteKey, acceptedQuote.key)

            if (!cardInfo?.autoConfirm) {
              void setupPaymentMethod({
                subscriberKey: currentUser?.key ?? '',
              })
            }
            setDialogControlProps(dialogControlIntialState)
          } else {
            enqueueSnackbar(
              `Quote acceptance failed: ${data.quoteAccept.message}`,
              { variant: 'error' },
            )
          }
        },
        onError: (error) => {
          enqueueSnackbar(
            `Quote acceptance failed: ${error.message}, please try again in a few seconds`,
            { variant: 'error' },
          )
        },
      })
    }
  }, [cardInfo, acceptedQuote])

  const handleQuoteRejection = (quoteKey: string): void => {
    setDialogControlProps((currentDialogControlProps) => {
      if (currentDialogControlProps.textBoxContent !== undefined) {
        void declineQuote({
          variables: {
            quoteKey,
            reason: currentDialogControlProps.textBoxContent,
          },
          onCompleted: (data) => {
            if (data.quoteDecline.success) {
              setDialogControlProps(dialogControlIntialState)
              enqueueSnackbar('Quote declined successfully.', {
                variant: 'success',
              })
            } else {
              enqueueSnackbar(
                `Decline quote failed: ${data.quoteDecline.message}`,
                { variant: 'error' },
              )
            }
            setDialogControlProps(dialogControlIntialState)
          },
          onError: (error) => {
            enqueueSnackbar(
              `Decline quote failed: ${error.message}, please try again in a few seconds`,
              { variant: 'error' },
            )
            setDialogControlProps(dialogControlIntialState)
          },
        })
        return currentDialogControlProps
      } else {
        return {
          ...currentDialogControlProps,
          errors: {
            message: 'Please provide a reason for declining the quote.',
          },
        }
      }
    })
  }

  const handleOpenRejectionModal = (quoteKey: string): void => {
    setDialogControlProps((prevState) => ({
      ...prevState,
      open: true,
      title: (
        <>
          <Typography variant="h1" gutterBottom>
            Confirm Decline Quote
          </Typography>
          <Typography variant="h2" gutterBottom>
            Please state why are you declining this quote.
          </Typography>
        </>
      ),
      content: (
        <>
          {!!dialogControlProps.errors?.message && (
            <Alert variant="outlined" severity="error">
              {dialogControlProps.errors.message}
            </Alert>
          )}
          <TextareaAutosize
            minRows={10}
            style={{ width: '97%' }}
            data-testid="decline-dialog-textarea"
            onChange={(e) => {
              setDialogControlProps((prevDialogControlProps) => ({
                ...prevDialogControlProps,
                textBoxContent: e.target.value,
              }))
            }}
          />
        </>
      ),
      actions: (
        <>
          <Button
            onClick={() => {
              setDialogControlProps(dialogControlIntialState)
            }}
            autoFocus
            variant="contained"
            color="error"
            data-testid="confirm-dialog-btn-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleQuoteRejection(quoteKey)
            }}
            loading={quoteLoading}
            autoFocus
            variant="contained"
            color="primary"
            data-testid="confirm-dialog-btn-accept"
          >
            Confirm
          </Button>
        </>
      ),
    }))
  }

  return (
    <Box sx={sx}>
      <Typography variant="h2" gutterBottom>
        Quote
      </Typography>
      {(quotes?.length == null || quotes.length == 0) && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" icon={false}>
            <Typography variant="h5" gutterBottom>
              No quotes have been created yet.
            </Typography>
            <Typography variant="body1" gutterBottom>
              You can create quotes to bill your clients for legal services.
            </Typography>
          </Alert>
        </Box>
      )}
      {quotes?.length > 0 &&
        quotes.map((quote) => (
          <QuoteListItem
            key={quote.key}
            currentUserAclIdentities={currentUser?.aclIdentities}
            quote={quote}
            readOnly={readOnly}
            isSending={quoteLoading}
            onClickAccept={() => handleQuoteAcceptConfirmation(quote)}
            onClickReject={() => handleOpenRejectionModal(quote.key)}
          />
        ))}
      <ConfirmationDialog
        open={dialogControlProps.open}
        onClose={() => {
          setDialogControlProps(dialogControlIntialState)
        }}
        title={dialogControlProps.title}
        content={dialogControlProps.content}
        actions={dialogControlProps.actions}
        errorMessage={dialogControlProps?.errors?.message}
      />
    </Box>
  )
}

export default SubscriberQuoteAdmin
